import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, adminApi } from "../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-[#98604A] text-white",
  quoted: "bg-[#3F5746] text-white",
  paid: "bg-[#232323] text-white",
  cancelled: "bg-[#DCD9CE] text-[#5C5C5C]",
};

export default function Admin() {
  const [password, setPassword] = useState(localStorage.getItem("admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [finalPrice, setFinalPrice] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (password) attemptLogin(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attemptLogin = async (pw) => {
    try {
      await api.post("/admin/login", { password: pw });
      localStorage.setItem("admin_pw", pw);
      setAuthed(true);
      loadAll(pw);
    } catch {
      localStorage.removeItem("admin_pw");
      setAuthed(false);
    }
  };

  const loadAll = async (pw) => {
    setLoading(true);
    try {
      const client = adminApi(pw);
      const [q, s] = await Promise.all([
        client.get("/admin/quotes"),
        client.get("/admin/stats"),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    if (!selected || !finalPrice) return;
    try {
      const client = adminApi(password);
      const { data } = await client.post(`/admin/quotes/${selected.id}/finalize`, {
        final_price: parseFloat(finalPrice),
      });
      toast.success("Quote finalized");
      const link = `${window.location.origin}${data.public_path}`;
      navigator.clipboard?.writeText(link).catch(() => {});
      toast.message("Payment link copied to clipboard", { description: link });
      setSelected(null);
      setFinalPrice("");
      loadAll(password);
    } catch {
      toast.error("Could not finalize");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this quote request?")) return;
    try {
      await adminApi(password).delete(`/admin/quotes/${id}`);
      toast.success("Deleted");
      loadAll(password);
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyLink = (q) => {
    const link = `${window.location.origin}/quote/${q.public_token}`;
    navigator.clipboard?.writeText(link);
    toast.success("Link copied");
  };

  const logout = () => {
    localStorage.removeItem("admin_pw");
    setAuthed(false);
    setPassword("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center px-6">
        <form
          onSubmit={(e) => { e.preventDefault(); attemptLogin(password); }}
          className="bg-white border border-[#DCD9CE] p-10 w-full max-w-sm"
          data-testid="admin-login-form"
        >
          <p className="overline mb-3 text-[#98604A]">Admin</p>
          <h1 className="font-serif-heading text-3xl mb-6">Sign in</h1>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field mb-4"
            data-testid="admin-password-input"
            autoFocus
          />
          <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-login-button">
            Enter
          </button>
          <Link to="/" className="text-xs text-[#5C5C5C] mt-4 inline-block hover:text-[#232323]">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#DCD9CE]">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-serif-heading text-xl">Heritage Stone Care</Link>
            <span className="overline text-[#98604A]">· Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => loadAll(password)} className="text-sm text-[#5C5C5C] hover:text-[#232323]" data-testid="admin-refresh">
              Refresh
            </button>
            <button onClick={logout} className="text-sm text-[#5C5C5C] hover:text-[#232323]" data-testid="admin-logout">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-12">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
            {[
              { l: "Total", v: stats.total_quotes },
              { l: "New", v: stats.new },
              { l: "Quoted", v: stats.quoted },
              { l: "Paid", v: stats.paid },
              { l: "Revenue", v: `$${stats.revenue.toFixed(0)}` },
            ].map((s) => (
              <div key={s.l} className="bg-white border border-[#DCD9CE] p-5">
                <p className="overline text-[#98604A] mb-1">{s.l}</p>
                <p className="font-serif-heading text-3xl">{s.v}</p>
              </div>
            ))}
          </div>
        )}

        <p className="overline text-[#98604A] mb-4">Quote Requests</p>

        <div className="bg-white border border-[#DCD9CE] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#EBE8E0] text-left">
              <tr>
                <th className="px-5 py-3 overline">Customer</th>
                <th className="px-5 py-3 overline">Cemetery</th>
                <th className="px-5 py-3 overline">Size</th>
                <th className="px-5 py-3 overline">Est.</th>
                <th className="px-5 py-3 overline">Final</th>
                <th className="px-5 py-3 overline">Status</th>
                <th className="px-5 py-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-[#5C5C5C]">Loading…</td></tr>
              )}
              {!loading && quotes.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-[#5C5C5C]">No quote requests yet.</td></tr>
              )}
              {quotes.map((q) => (
                <tr key={q.id} className="border-t border-[#DCD9CE] hover:bg-[#F7F6F2]/60" data-testid={`quote-row-${q.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium">{q.name}</div>
                    <div className="text-xs text-[#5C5C5C]">{q.email}</div>
                    <div className="text-xs text-[#5C5C5C]">{q.phone}</div>
                  </td>
                  <td className="px-5 py-4 text-[#5C5C5C]">{q.cemetery_name}</td>
                  <td className="px-5 py-4 capitalize">{q.stone_size}</td>
                  <td className="px-5 py-4">${q.estimated_price}</td>
                  <td className="px-5 py-4">{q.final_price ? `$${q.final_price}` : "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-[10px] tracking-widest uppercase ${STATUS_COLORS[q.status]}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => { setSelected(q); setFinalPrice(q.final_price || q.estimated_price); }}
                        className="text-xs text-[#3F5746] hover:underline"
                        data-testid={`quote-finalize-${q.id}`}
                      >
                        {q.final_price ? "Edit" : "Finalize"}
                      </button>
                      {q.final_price && (
                        <button
                          onClick={() => copyLink(q)}
                          className="text-xs text-[#3F5746] hover:underline"
                          data-testid={`quote-copy-${q.id}`}
                        >
                          Copy link
                        </button>
                      )}
                      <button
                        onClick={() => remove(q.id)}
                        className="text-xs text-[#B04E4E] hover:underline"
                        data-testid={`quote-delete-${q.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Finalize modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-6"
          onClick={() => setSelected(null)}
          data-testid="finalize-modal"
        >
          <div
            className="bg-white max-w-lg w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="overline text-[#98604A] mb-3">Finalize Quote</p>
            <h2 className="font-serif-heading text-3xl mb-2">{selected.name}</h2>
            <p className="text-sm text-[#5C5C5C] mb-6">
              {selected.cemetery_name} · {selected.stone_size} · preliminary ${selected.estimated_price}
            </p>
            {selected.notes && (
              <div className="bg-[#EBE8E0] p-4 mb-6 text-sm">
                <p className="overline mb-1">Customer notes</p>
                <p className="text-[#3a3a3a] whitespace-pre-line">{selected.notes}</p>
              </div>
            )}
            <label className="overline mb-2 block">Final price (USD)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-field mb-6"
              value={finalPrice}
              onChange={(e) => setFinalPrice(e.target.value)}
              data-testid="finalize-price-input"
              autoFocus
            />
            <div className="flex gap-3">
              <button onClick={finalize} className="btn-primary flex-1 justify-center" data-testid="finalize-confirm">
                Generate payment link
              </button>
              <button onClick={() => setSelected(null)} className="btn-secondary">
                Cancel
              </button>
            </div>
            <p className="text-xs text-[#5C5C5C] mt-4">
              The customer's payment link will be copied to your clipboard so you can email it to them.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
